import React, { useEffect, useState } from "react";
import { Play, Users, Star, Crown, DollarSign, Percent, Repeat, Eye, ExternalLink, Download } from "lucide-react";
import api from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDay = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return s; } };

const exportCsv = (d) => {
  const s = d.store || {};
  const rows = [
    ["Metric", "Value"],
    ["Game", d.title],
    ["Status", d.status],
    ["Total plays", d.plays],
    ["Unique players", d.unique_players],
    ["Returning players", d.returning_players],
    ["Returning %", d.returning_pct],
    ["Rating", d.rating],
    ["Rating count", d.rating_count],
    ["Buyers", d.buyers],
    ["Revenue (USD)", Number(d.revenue || 0).toFixed(2)],
    ["Conversion %", d.conversion],
    ["Store views", s.views || 0],
    ["Store plays", s.plays || 0],
    ["QR scans", s.scans || 0],
    ["Shares", s.shares || 0],
    [],
    ["Date", "Plays (logged-in)"],
    ...(d.plays_trend || []).map((p) => [p.date, p.plays]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(d.title || "game").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-analytics.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}
    </div>
    <div className="font-display text-xl font-extrabold tabular-nums mt-1">{value}</div>
  </div>
);

const RatingSparkline = ({ trend }) => {
  const pts = trend || [];
  const n = pts.length;
  const vals = pts.map((p) => p.avg);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const W = 560, H = 60, pad = 6;
  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
  const y = (v) => H - pad - ((v - min) / span) * (H - 2 * pad);
  const line = pts.map((p, i) => `${x(i)},${y(p.avg)}`).join(" ");
  const cur = pts[n - 1];
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none" data-testid="ga-rating-trend">
        <polyline points={line} fill="none" stroke="#eab308" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.avg)} r="2.5" fill="#eab308" />)}
      </svg>
      <div className="flex justify-between text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
        <span>{pts[0].label}</span>
        <span>Now {cur.avg}★ · {cur.count} ratings</span>
        <span>{cur.label}</span>
      </div>
    </div>
  );
};


const TrendChart = ({ trend }) => {
  const pts = trend || [];
  const n = pts.length;
  const max = Math.max(1, ...pts.map((p) => p.plays));
  const W = 560, H = 90, pad = 8;
  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
  const y = (v) => H - pad - (v / max) * (H - 2 * pad);
  const line = pts.map((p, i) => `${x(i)},${y(p.plays)}`).join(" ");
  const area = `${pad},${H - pad} ${line} ${x(n - 1)},${H - pad}`;
  const total = pts.reduce((a, b) => a + b.plays, 0);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none" data-testid="ga-plays-trend">
        <polygon points={area} fill="url(#gaGrad)" opacity="0.35" />
        <polyline points={line} fill="none" stroke="#3b9eff" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <defs>
          <linearGradient id="gaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b9eff" /><stop offset="100%" stopColor="#3b9eff" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <div className="flex justify-between text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
        <span>{n ? fmtDay(pts[0].date) : ""}</span>
        <span>{total} logged-in plays · 14 days</span>
        <span>{n ? fmtDay(pts[n - 1].date) : ""}</span>
      </div>
    </div>
  );
};

export default function GameAnalyticsSheet({ gameId, open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !gameId) return;
    setData(null);
    api.get(`/creator/game/${gameId}/analytics`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [open, gameId]);

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="game-analytics-sheet">
        {!data ? (
          <>
            <SheetTitle className="sr-only">Game analytics</SheetTitle>
            <div className="min-h-[40vh] grid place-items-center"><Spinner /></div>
          </>
        ) : data.error ? (
          <>
            <SheetTitle className="sr-only">Game analytics</SheetTitle>
            <div className="py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">Couldn't load analytics for this game.</div>
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-lg">
                <span className="text-2xl">{data.cover_emoji}</span>
                <span className="truncate" data-testid="ga-title">{data.title}</span>
                {data.premium && <Crown className="w-4 h-4 text-amber-500 shrink-0" />}
              </SheetTitle>
              <SheetDescription className="capitalize">{data.status} · per-game analytics</SheetDescription>
            </SheetHeader>

            <Button variant="outline" size="sm" className="rounded-full mt-4 w-full" onClick={() => exportCsv(data)} data-testid="ga-export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Stat icon={Play} label="Total plays" value={data.plays.toLocaleString()} accent="#3b9eff" />
              <Stat icon={Users} label="Unique players" value={data.unique_players.toLocaleString()} accent="#a855f7" />
              <Stat icon={Repeat} label="Returning" value={`${data.returning_players} · ${data.returning_pct}%`} accent="#14b8a6" />
              <Stat icon={Star} label="Rating" value={data.rating_count ? `${data.rating}★ (${data.rating_count})` : "—"} accent="#eab308" />
            </div>

            <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Plays trend</div>
              <TrendChart trend={data.plays_trend} />
            </div>

            {data.rating_trend?.length >= 2 && (
              <div className="mt-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">
                  <Star className="w-3.5 h-3.5 text-amber-500" /> Rating over time
                </div>
                <RatingSparkline trend={data.rating_trend} />
              </div>
            )}

            {data.premium && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Stat icon={Crown} label="Buyers" value={data.buyers} accent="#f59e0b" />
                <Stat icon={Percent} label="Conversion" value={`${data.conversion}%`} accent="#a855f7" />
                <Stat icon={DollarSign} label="Revenue" value={usd(data.revenue)} accent="#22c55e" />
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="ga-store">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">
                <Eye className="w-3.5 h-3.5" /> Store page
              </div>
              {data.store?.has_store ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div><div className="font-display text-lg font-extrabold tabular-nums">{data.store.views}</div><div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Views</div></div>
                  <div><div className="font-display text-lg font-extrabold tabular-nums">{data.store.plays}</div><div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Plays</div></div>
                  <div><div className="font-display text-lg font-extrabold tabular-nums">{data.store.scans}</div><div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">QR scans</div></div>
                  <div><div className="font-display text-lg font-extrabold tabular-nums">{data.store.shares}</div><div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Shares</div></div>
                </div>
              ) : (
                <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /> No public store page yet — build one in the Go-To-Market Kit to track views.
                </p>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
